import type { components } from '@connected/api-client/api.generated';

import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@connected/ui/components/dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Outlet, useLocation, useMatchRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumb } from '@/components/breadcrumb';
import { SubmenuNavigation } from '@/components/submenu-navigation';

export const Route = createFileRoute('/_public/customer/locations/$locationId/electrical/projects/$projectId/_overview')({
    component: RouteComponent,
});

type ListElectricalProjectPortalQuery = components['schemas']['ListElectricalProjectPortalQueryResultProject'];

function RouteComponent() {
    const auth = useAuthentication();
    const { t } = useTranslation('customer-module-electrical');
    const navigate = useNavigate();
    const params = Route.useParams();
    const search = Route.useSearch();
    const location = useLocation();
    const matchRoute = useMatchRoute();

    const objectQuery = useQuery({
        ...portaalQueryOptions.portal.objects.view({ ObjectId: params.locationId }),
        enabled: !!auth.isAuthenticated && !!params.locationId,
    });

    const projectsQuery = useQuery({
        ...portaalQueryOptions.electrical.project.list({ ObjectId: params.locationId }),
        enabled: !!auth.isAuthenticated && !!params.locationId,
    });

    const projectQuery = useQuery({
        ...portaalQueryOptions.electrical.project.view({ ElectricalProjectId: params.projectId }),
        enabled: !!params.projectId,
    });

    const containsScope12Inspection = useMemo(() => {
        if (projectQuery?.isFetched && projectQuery.isSuccess && projectQuery?.data?.Project?.Templates && projectQuery?.data?.Project?.Templates.length > 0) {
            return projectQuery?.data?.Project?.Templates?.some(row => row?.InspectionKind === 'Scope12');
        }
        return false;
    }, [projectQuery?.data?.Project?.Templates, projectQuery?.isFetched, projectQuery.isSuccess]);

    const filterProjectNames = useCallback((project: ListElectricalProjectPortalQuery) => {
        // TODO: When the Projects List Endpoint contains InspectionKind, make all the .Name instead .InspectionKind.
        // and use the other return for length 1. A ticket is created for this.

        if (project?.Templates && project?.Templates?.length > 1) {
            if (project?.Templates?.every(x => x.InspectionKind?.toString().toLocaleLowerCase().includes('scope'))) {
                const templates = project.Templates.sort((a, b) => {
                    const an = a.InspectionKind?.match(/\d+/)?.[0];
                    const bn = b.InspectionKind?.match(/\d+/)?.[0];

                    return an && bn ? Number(an) - Number(bn) : an ? -1 : bn ? 1 : (a.InspectionKind ?? '').localeCompare(b.InspectionKind ?? '');
                }).map(template => ({
                    ...template,
                    InspectionKind: template?.InspectionKind?.replace(/\D+/g, ''),
                }));
                return `Scope ${templates?.map(x => x.InspectionKind).join('-')}`;
            }
            return `${project?.Templates?.map(x => x.InspectionKind).join('-')}`;
        }
        if (project?.Templates?.length === 1) {
            return `${project?.Templates?.[0]?.InspectionKind?.replace(/([A-Z])(?=\d)/gi, '$1 ') ?? t('module-electrical.overview.attached-files-table.columns.inspection')}`;
        }
        return t('module-electrical.overview.attached-files-table.columns.inspection');
    }, [t]);

    const projectsList = useMemo(() => {
        if (projectsQuery.isSuccess && projectQuery.isFetched) {
            return projectsQuery?.data?.Projects?.map((row: ListElectricalProjectPortalQuery) => ({
                ...row,
                DisplayName: filterProjectNames(row),
            }));
        }
    }, [filterProjectNames, projectQuery.isFetched, projectsQuery?.data?.Projects, projectsQuery.isSuccess]);

    const hideSubmenuForPvInventerItem = matchRoute({
        from: '/customer/locations/$locationId/electrical/projects/$projectId/pv-installations/$pvInverterId',
    });

    const hideSubmenuForPvInvenerObservations = matchRoute({
        from: '/customer/locations/$locationId/electrical/projects/$projectId/pv-installations/$pvInverterId/observations/$observationId',
    });

    const hideMobileOverview = matchRoute({
        from: '/customer/locations/$locationId/electrical/projects/$projectId/overview',
    });

    return (
        <div className="max-w-screen-2xl flex flex-col flex-1 p-8 pb-0 overflow-y-auto h-full">
            {(!hideSubmenuForPvInventerItem && !hideSubmenuForPvInvenerObservations) && (
                <div>
                    <Breadcrumb
                        items={[
                            {
                                name: auth.isAuthenticated
                                    ? objectQuery.data?.Object?.Name || t('module-electrical.projects.title')
                                    : projectQuery.data?.Project?.ProjectObject?.Name || t('module-electrical.projects.title'),
                                to: '/customer/locations/$locationId',
                                params: { locationId: params.locationId },
                            },
                            {
                                isActive: true,
                                name: t('module-electrical.name'),
                                subcategory: location.pathname.split('/').pop() !== 'overview'
                                    ? {
                                            name: t(`module-electrical.${location.pathname.split('/').pop()}.title`),
                                            to: `/customer/locations/$locationId/electrical/projects/$projectId/${location.pathname.split('/').pop()}`,
                                            params: {
                                                locationId: params.locationId,
                                                projectId: params.projectId,
                                            },
                                        }
                                    : undefined,
                            },
                        ]}
                    />
                    <div className="mb-6">
                        <div className="flex flex-col max-sm:flex-wrap gap-2 justify-start w-fit">
                            <h1 className="text-2xl font-semibold">
                                {t('module-electrical.title')}
                            </h1>
                            <div className="flex gap-2 md:gap-4 items-center">
                                {t('module-electrical.your-viewing-info-about')}
                                <DropdownMenu>
                                    <DropdownMenuTrigger disabled={!auth.isAuthenticated} className="flex text-sm items-center gap-2 border-2 border-primary/60 px-3 rounded-lg h-10 bg-background-primary text-primary  hover:bg-primary/10 justify-center">
                                        {projectQuery.data?.Project?.Templates?.[0]?.InspectionKind?.replace(/([a-z])(?=\d)/gi, '$1')
                                            ? (
                                                    <>
                                                        <span>{projectsList?.find(row => row.ElectricalProjectId === params.projectId)?.DisplayName || filterProjectNames(projectQuery?.data?.Project)}</span>
                                                        {projectQuery.data.Project?.EndDate && (
                                                            <span>
                                                                (
                                                                {new Date(projectQuery.data.Project.EndDate).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                                )
                                                            </span>
                                                        )}
                                                    </>
                                                )
                                            : t('module-electrical.select-project')}
                                        <ChevronDown className="w-5 h-5" />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="flex flex-col" align="start">
                                        {projectsList?.filter(row => row.ElectricalProjectId !== params.projectId).map(project => (
                                            <DropdownMenuItem
                                                key={project.ElectricalProjectId}
                                                className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                                onClick={() => {
                                                    navigate({
                                                        to: `/customer/locations/$locationId/electrical/projects/$projectId/${location.pathname.split('/').pop()}`,
                                                        params: {
                                                            locationId: params.locationId,
                                                            projectId: project.ElectricalProjectId!,
                                                        },
                                                        search,
                                                    });
                                                }}
                                            >
                                                {project.DisplayName}
                                                {' '}
                                                {project.EndDate ? `(${new Date(project.EndDate).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })})` : ''}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                            {/* {auth.isAuthenticated && ( */}

                            {/* )} */}
                        </div>
                    </div>
                    <div className="hidden sm:block">
                        <SubmenuNavigation
                            items={[
                                {
                                    label: t('module-electrical.overview.title'),
                                    to: '/customer/locations/$locationId/electrical/projects/$projectId/overview' as const,
                                    params: { locationId: params.locationId, projectId: params.projectId },
                                    search,
                                },
                                {
                                    label: t('module-electrical.distribution-boards.title'),
                                    to: '/customer/locations/$locationId/electrical/projects/$projectId/distribution-boards' as const,
                                    params: { locationId: params.locationId, projectId: params.projectId },
                                    search,
                                },
                                {
                                    label: t('module-electrical.control-boxes.title'),
                                    to: '/customer/locations/$locationId/electrical/projects/$projectId/final-circuit' as const,
                                    params: { locationId: params.locationId, projectId: params.projectId },
                                    search,
                                },
                                ...(containsScope12Inspection
                                    ? [
                                            {
                                                label: t('module-electrical.pv-installations.title'),
                                                to: '/customer/locations/$locationId/electrical/projects/$projectId/pv-installations' as const,
                                                params: { locationId: params.locationId, projectId: params.projectId },
                                                search,
                                            },
                                        ]
                                    : []),
                            ]}
                        />
                    </div>
                    <div className="sm:hidden !text-sm">
                        {(!hideMobileOverview && containsScope12Inspection) && (
                            <div className="mb-4">
                                <Link
                                    to="/customer/locations/$locationId/electrical/projects/$projectId/overview"
                                    params={{ locationId: params.locationId, projectId: params.projectId }}
                                    search={search}
                                >
                                    <Button
                                        variant="ghost"
                                        className="flex items-center gap-2 px-0 text-primary hover:text-primary/80"
                                    >
                                        <ArrowLeft className="w-5 h-5" />
                                        {t('module-electrical.overview.title')}
                                    </Button>
                                </Link>
                            </div>
                        )}
                        <SubmenuNavigation
                            items={[
                                ...(!containsScope12Inspection
                                    ? [
                                            {
                                                label: t('module-electrical.overview.title'),
                                                to: '/customer/locations/$locationId/electrical/projects/$projectId/overview' as const,
                                                params: { locationId: params.locationId, projectId: params.projectId },
                                                search,
                                            },
                                        ]
                                    : []),
                                {
                                    label: t('module-electrical.distribution-boards.title'),
                                    to: '/customer/locations/$locationId/electrical/projects/$projectId/distribution-boards' as const,
                                    params: { locationId: params.locationId, projectId: params.projectId },
                                    search,
                                },
                                {
                                    label: t('module-electrical.control-boxes.title'),
                                    to: '/customer/locations/$locationId/electrical/projects/$projectId/final-circuit' as const,
                                    params: { locationId: params.locationId, projectId: params.projectId },
                                    search,
                                },
                                ...(containsScope12Inspection
                                    ? [
                                            {
                                                label: t('module-electrical.pv-installations.title'),
                                                to: '/customer/locations/$locationId/electrical/projects/$projectId/pv-installations' as const,
                                                params: { locationId: params.locationId, projectId: params.projectId },
                                                search,
                                            },
                                        ]
                                    : []),
                            ]}
                        />
                    </div>
                </div>
            )}
            <Outlet />
        </div>
    );
}
